import React, { Component } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Box,
    Slide,
    Typography
} from "@mui/material";
import moment from "moment";
import { DateRangePicker } from 'react-date-range';
import { TransitionProps } from "@mui/material/transitions";
import { ShowAlert } from "../ShowAlert";
import PromoCodeServices from "../../Services/PromoCodeService";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import PromotionalCode from "../Reusables/CodeGenerator";
import InputTextField from "../Reusables/InputTextField";
import { Messages } from "../../Component/Reusables/Utility";
import { amountFormat } from "../helper";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface EditPromoCodeProps {
    open: boolean;
    accessToken: string;
    selectedPromoData: any;
    onClose?: (state: boolean, action: string) => void;
    refreshData: () => void;
    currentTab: string;
}

interface EditPromoCodeState {
    promoCode: string;
    amount: string;
    minTransactionAmt: string;
    applicableFirstTransaction: boolean;
    selectedDateRange: { startDate: Date; endDate: Date };
    isCodeValid: boolean;
    amountError: string;
    minTransactionAmtError: string;
    _id: string;
}

function getCode(data: any): string {
    return data?.promo_code || data?.coupon_code || "";
}

function getId(data: any): string {
    return data?.promo_code_id || data?.cashbackCouponId || "";
}

class EditPromoCode extends Component<EditPromoCodeProps, EditPromoCodeState> {
    constructor(props: EditPromoCodeProps) {
        super(props);
        const { selectedPromoData } = props;
        this.state = {
            _id: getId(selectedPromoData),
            promoCode: getCode(selectedPromoData),
            amount: selectedPromoData?.amount || "",
            minTransactionAmt: selectedPromoData?.minTransactionAmt || "",
            applicableFirstTransaction: selectedPromoData?.applicableFirstTransaction === 1,
            selectedDateRange: {
                startDate: new Date(selectedPromoData?.startTime),
                endDate: new Date(selectedPromoData?.endTime),
            },
            isCodeValid: false,
            amountError: "",
            minTransactionAmtError: "",
        };
    }

    handleSave = async () => {
        const {
            _id,
            promoCode,
            amount,
            minTransactionAmt,
            applicableFirstTransaction,
            selectedDateRange
        } = this.state;

        const { accessToken } = this.props;

        // Step 1: Sanitize and convert to number for validation
        const numericAmount = amount ? parseFloat(amount.toString().replace(/,/g, "")) : 0;
        const numericMinTransactionAmt = minTransactionAmt ? parseFloat(minTransactionAmt.toString().replace(/,/g, "")) : 0;

        // Step 2: Perform numeric validation
        let amountError = "";
        let minTransactionAmtError = "";

        if (numericAmount <= 0) {
            amountError = Messages.Errors.couponAmount;
        }

        if (numericMinTransactionAmt < numericAmount) {
            minTransactionAmtError = Messages.Errors.minTransactionAmt;
        }

        // Step 3: Handle errors if any
        if (amountError || minTransactionAmtError) {
            this.setState({ amountError, minTransactionAmtError });
            return;
        }

        // Step 4: Convert to sanitized string for API payload
        const sanitizedAmount = numericAmount.toString();
        const sanitizedMinTransactionAmt = numericMinTransactionAmt.toString();

        const { currentTab } = this.props;
        const isPromoCode = currentTab === "promo_code";

        const payload = {
            [isPromoCode ? "promoCodeId" : "couponCodeId"]: _id,
            [isPromoCode ? "promoCode" : "couponCode"]: promoCode,
            amount: sanitizedAmount,
            minTransactionAmt: sanitizedMinTransactionAmt,
            ...(isPromoCode && { applicableFirstTransaction }),
            startTime: moment(selectedDateRange.startDate).startOf("day").utc().format(),
            endTime: moment(selectedDateRange.endDate).endOf("day").utc().format(),
        };

        try {
            let response;
            if (isPromoCode) {
                response = await PromoCodeServices.editpromocode(accessToken, payload);
            } else {
                response = await PromoCodeServices.editcoupon(accessToken, payload);
            }
            if (response?.ok) {
                ShowAlert("Success", "Details updated successfully", "success");
                this.props.refreshData();
                this.props.onClose?.(true, "ok");
            } else {
                ShowAlert("Error", "Failed to update promo code", "error");
            }
        } catch (error) {
            console.error("Error updating promo code", error);
            ShowAlert("Error", "Something went wrong", "error");
        }
    };

    //Update generated code
    onGenerate = (newCode: string) => {
        console.log("New code:", newCode);
        this.setState({ promoCode: newCode, isCodeValid: false });
    };

    // Handle promo code change & validation
    handleOnChanage = (newCode: string, isCodeValid: boolean) => {
        this.setState({
            promoCode: newCode,
            isCodeValid: isCodeValid, // Only valid if greter than 4 characters
        });
    };

    handleLimitChange = (field: keyof EditPromoCodeState, value: string) => {
        let sanitizedValue = value.replace(/[^0-9.]/g, "");
        const MAX_LIMIT = 999999999;

        // Prevent multiple decimal points
        if ((sanitizedValue.match(/\./g) || []).length > 1) {
            sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf(".") + 1);
        }

        // Ensure value does not exceed max limit
        if (parseFloat(sanitizedValue) > MAX_LIMIT) {
            sanitizedValue = MAX_LIMIT.toString();
        }

        let amountError = "";
        let minTransactionAmtError = "";

        switch (field) {
            case "amount":
                if (sanitizedValue === "" || sanitizedValue === "0.00" || parseFloat(sanitizedValue) === 0) {
                    amountError = Messages.Errors.couponAmount
                }
                break;

            case "minTransactionAmt":
                if (
                    sanitizedValue === "" ||
                    sanitizedValue === "0.00" ||
                    parseFloat(sanitizedValue) === 0 ||
                    (this.state.amount && parseFloat(sanitizedValue) < parseFloat(this.state.amount))
                ) {
                    minTransactionAmtError = Messages.Errors.minTransactionAmt;
                }
                break;

            default:
                break;
        }

        this.setState((prevState) => ({
            ...prevState,
            [field]: sanitizedValue,
            amountError,
            minTransactionAmtError
        }));
    };

    // Handle Amount blur
    handleLimitBlur = (field: keyof EditPromoCodeState) => {
        let value = this.state[field];

        if (typeof value !== "string") {
            value = "0";
        }

        if (!value || isNaN(parseFloat(value))) {
            value = "0";
        }
        const sanitizedValue = value.replace(/,/g, "");
        const updatedAmount = amountFormat(parseFloat(sanitizedValue), "en-US", "USD").replace("$", "");

        this.setState((prevState) => ({
            ...prevState,
            [field]: updatedAmount,
        }));
    };

    // Handle Amount focus
    handleLimitFocus = (field: keyof EditPromoCodeState) => {
        const value = this.state[field];
        if (typeof value === 'string') {
            const n_ = value.replace(/,/g, "");
            this.setState((prevState) => ({
                ...prevState,
                [field]: n_,
                [`${field}InputType`]: "number"
            }));
        }
    };

    render() {
        const { open, onClose } = this.props;
        const { promoCode, amount, minTransactionAmt, applicableFirstTransaction, selectedDateRange, amountError, minTransactionAmtError } = this.state;
        const { currentTab } = this.props;
        const isPromoCode = currentTab === "promo_code";

        return (
            <Dialog open={open} onClose={() => onClose?.(false, "cancel")} fullWidth maxWidth="sm" TransitionComponent={Transition}>
                <DialogTitle>{this.props.currentTab === "promo_code" ? "Edit Promo Code" : "Edit Cashback coupon"}</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={1} mt={1}>
                        <PromotionalCode
                            value={promoCode}
                            placeholder="Enter code or generate"
                            onGenerate={this.onGenerate}
                            onChange={this.handleOnChanage}
                        />
                        <InputTextField
                            value={amount}
                            startAdornment="$"
                            inputType="number"
                            onChange={(value) => this.handleLimitChange("amount", value)}
                            onBlur={() => this.handleLimitBlur("amount")}
                            onFocus={() => this.handleLimitFocus('amount')}
                            error={!!amountError}
                            helperText={amountError}
                            placeholder="Enter Amount"
                            filedLable="Amount"
                        />
                        <InputTextField
                            value={minTransactionAmt}
                            startAdornment="$"
                            inputType="number"
                            onChange={(value) => this.handleLimitChange("minTransactionAmt", value)}
                            onBlur={() => this.handleLimitBlur("minTransactionAmt")}
                            onFocus={() => this.handleLimitFocus('minTransactionAmt')}
                            error={!!minTransactionAmtError}
                            helperText={minTransactionAmtError}
                            placeholder="Min transaction"
                            filedLable="Min transaction"
                        />
                        {isPromoCode && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={this.state.applicableFirstTransaction}
                                        onChange={e =>
                                            this.setState({
                                                applicableFirstTransaction: e.target.checked
                                            })
                                        }
                                    />
                                }
                                label="Eligible for first transaction"
                            />
                        )}
                        <Card variant="outlined" sx={{ mt: isPromoCode ? 0 : 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                            <CardContent>
                                <Typography variant="body2">
                                    <strong>Valid From:</strong> {selectedDateRange.startDate?.toLocaleDateString() || '-'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Valid Till:</strong> {selectedDateRange.endDate?.toLocaleDateString() || '-'}
                                </Typography>
                                <Box mt={1}>
                                    <DateRangePicker
                                        ranges={[{
                                            startDate: selectedDateRange.startDate,
                                            endDate: selectedDateRange.endDate,
                                            key: 'selection'
                                        }]}
                                        onChange={(ranges) => {
                                            const { selection } = ranges;
                                            if (selection.startDate && selection.endDate) {
                                                this.setState({
                                                    selectedDateRange: {
                                                        startDate: selection.startDate,
                                                        endDate: selection.endDate,
                                                    },
                                                });
                                            }
                                        }}
                                        minDate={new Date()}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onClose?.(false, "cancel")}>Cancel</Button>
                    <Button onClick={this.handleSave} color="secondary" variant="contained" disabled={this.state.isCodeValid}>Save</Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default EditPromoCode;
